// Proactive tips: small, timely nudges built from his age, the logs (however
// patchy), the care plan, the weather and the calendar. Each tip has a stable
// id per day so it can be dismissed and not nag.

import { SOCIAL_CATEGORIES } from '../content/socialisation.ts';
import { ageNow } from './age.ts';
import { careState, openCare } from './care.ts';
import { foodStock } from './feeding.ts';
import { sleepWithin, toiletHeatmap } from './status.ts';
import { DAY, HOUR, addDays, daysBetween, formatTime, today } from './time.ts';
import type { CareItem, Household, LogEvent, Profile } from './types.ts';
import { bestWalkHour, type Forecast } from './weather.ts';

export interface Tip {
  id: string;
  title: string;
  body: string;
  kind: 'weather' | 'toilet' | 'training' | 'social' | 'care' | 'health' | 'age' | 'food' | 'routine';
  priority: number;
  route?: string;
}

/** A tip for each week of puppyhood — what matters most right now. */
const WEEKLY: Record<number, [string, string]> = {
  8: ['Settling in', 'Keep the first days calm and boring: short sessions, lots of sleep, the same toilet spot every time. Visitors can wait.'],
  9: ['Handle ears and paws daily', 'Thirty seconds a day: touch an ear, a paw, lift a lip — treat each touch. Cocker grooming and vet visits for life get easier.'],
  10: ['Start “swap” games', 'Trade a toy for a treat and give it back. Solid-colour male cockers can be prone to guarding — preventing it starts now.'],
  11: ['Carry him into the city', 'Before he can walk in public, carry him to watch buses, trains and crowds for a few minutes. Calm watching counts.'],
  12: ['Three meals a day', 'Around 12 weeks drop to 3 meals — same daily amount, split three ways. Keep the last meal about two hours before bed.'],
  13: ['Book a puppy groom intro', 'Once he’s fully vaccinated, a short “puppy intro” at a groomer (bath, dryer noise, feet) builds confidence.'],
  14: ['Teething is coming', 'Adult teeth start pushing through from about 3½ months. Freeze a wet flannel or a puppy Kong for sore gums.'],
  15: ['Recall games indoors', 'Call him between you in the flat — “Arlo, come!” then a jackpot. Hundreds of easy wins now = a dog you can let off-lead later.'],
  16: ['Socialisation window closing', 'The sensitive period tapers off around now. Keep going with gentle new experiences — especially anything not yet ticked.'],
  18: ['Loose-lead walking', 'Short, reward-heavy sessions: reward him for being by your side, stop when the lead goes tight. Sniffing breaks are the reward.'],
  20: ['Keep a waistline', 'Cockers are food-obsessed. Weigh him, check he has a waist from above, and take training treats out of his daily ration.'],
  22: ['Whistle recall', 'Cockers are gundogs — a whistle (pip-pip-pip) carries further than your voice and sounds the same whoever blows it.'],
  24: ['Two meals soon', 'Around 6 months, move to 2 meals a day. Book the 6-month vet check and chat about neutering timing.'],
  26: ['Adolescence ahead', 'Expect some “selective hearing” from 6–18 months (peaking ~8 months). Go back to easier training and keep the long line on.'],
};

export interface TipInput {
  profile?: Profile;
  household?: Household;
  events: LogEvent[];
  care: CareItem[];
  forecast?: Forecast;
  now: number;
}

export function proactiveTips({ profile, household, events, care, forecast, now }: TipInput): Tip[] {
  const tips: Tip[] = [];
  const day = today(now);
  const age = profile ? ageNow(profile, now) : undefined;
  const weeks = age?.weeks ?? 10;
  const home = !profile?.homeDate || profile.homeDate <= day;
  const hour = new Date(now).getHours();
  const since = (type: LogEvent['type']) => events.filter((e) => e.type === type).reduce((m, e) => Math.max(m, e.at), 0);
  const anyLogs = events.some((e) => e.at > now - 3 * DAY);

  // --- Weather ---------------------------------------------------------------
  if (forecast && home) {
    const next3 = forecast.hours.filter((h) => h.time >= now && h.time <= now + 3 * HOUR);
    const wet = next3.find((h) => h.rainChance >= 60);
    const best = bestWalkHour(forecast, now);
    if (wet) {
      const dryNow = next3[0] && next3[0].rainChance < 40;
      tips.push({
        id: `rain-${day}-${new Date(wet.time).getHours()}`,
        kind: 'weather',
        priority: 70,
        title: dryNow ? `Rain from about ${formatTime(wet.time)}` : 'Wet out there',
        body: `${dryNow ? 'Go out now while it’s dry. ' : ''}${best && best.rainChance < wet.rainChance - 20 ? `Driest spell looks like ${formatTime(best.time)}. ` : ''}Towel him off and dry inside those ears after — damp cocker ears invite infections.`,
        route: '/walks',
      });
    }
    const hot = forecast.hours.find((h) => h.time >= now && h.time <= now + 8 * HOUR && h.tempC >= 24);
    if (hot) tips.push({ id: `heat-${day}`, kind: 'weather', priority: 80, title: 'Warm day', body: 'Walk early or late, carry water, and check pavements with the back of your hand. Never leave him in a car.', route: '/walks' });
    const icy = forecast.hours.find((h) => h.time >= now && h.time <= now + 12 * HOUR && h.tempC <= 0);
    if (icy) tips.push({ id: `ice-${day}`, kind: 'weather', priority: 40, title: 'Frost or ice later', body: 'Grit and salt sting paws and are harmful if licked — wipe his feet after walks.' });
  }

  // --- Care plan ----------------------------------------------------------------
  for (const c of openCare(care)) {
    if (!c.due) continue;
    const d = daysBetween(day, c.due);
    if (d === 1 && (c.kind === 'vet' || c.kind === 'vaccination'))
      tips.push({
        id: `vet-${c.id}`,
        kind: 'care',
        priority: 90,
        title: `Tomorrow: ${c.title}`,
        body: 'Take his vaccination card, some tasty treats and a towel. Skip breakfast only if the vet asks. Note any questions in Ask first.',
        route: '/health',
      });
  }
  const overdue = openCare(care).filter((c) => careState(c, now) === 'overdue');
  if (overdue.length === 1) tips.push({ id: `overdue-${overdue[0].id}`, kind: 'care', priority: 60, title: `${overdue[0].title} is waiting`, body: 'If it’s already done, tick it off so the next one gets scheduled.', route: '/health' });

  if (!home) return rank(tips);

  // --- Toilet pattern -------------------------------------------------------------
  const heat = toiletHeatmap(events, now, 14);
  const hotspot = heat.filter((h) => h.inside >= 2).sort((a, b) => b.inside - a.inside)[0];
  if (hotspot) {
    const at = (hotspot.hour + 23) % 24;
    tips.push({
      id: `accident-hour-${hotspot.hour}-${day}`,
      kind: 'toilet',
      priority: 75,
      title: `Accidents cluster around ${String(hotspot.hour).padStart(2, '0')}:00`,
      body: `${hotspot.inside} indoor accidents at that hour recently. Try a trip out at about ${String(at).padStart(2, '0')}:45 — before the usual moment.`,
      route: '/insights',
    });
  }

  // --- Sleep ----------------------------------------------------------------------
  const sleepLogs = events.filter((e) => e.type === 'sleep' && e.at > now - DAY).length;
  if (sleepLogs >= 3) {
    const slept = sleepWithin(events, now - DAY, now) / HOUR;
    if (slept < (weeks < 16 ? 15 : 13))
      tips.push({ id: `sleep-${day}`, kind: 'routine', priority: 55, title: 'Short on sleep?', body: `About ${Math.round(slept)} h logged in 24 h — puppies need 18–20. Overtired puppies bite more: try a crate nap after about an hour awake.` });
  }

  // --- Training & alone time -----------------------------------------------------
  const lastTraining = since('training');
  if (anyLogs && weeks >= 8 && now - lastTraining > 2 * DAY)
    tips.push({ id: `train-${day}`, kind: 'training', priority: 45, title: 'Two minutes of training?', body: 'A quick name game or hand-touch before his next meal. Little and often beats long sessions.', route: '/training' });
  const lastAlone = since('alone');
  if (anyLogs && weeks >= 9 && now - lastAlone > 3 * DAY)
    tips.push({ id: `alone-${day}`, kind: 'training', priority: 50, title: 'Practise alone time', body: 'Pop into another room for 30 seconds while he chews a Kong. Build up slowly — it’s the best protection against separation problems (and tenement neighbours).', route: '/training/alone' });

  // --- Socialisation --------------------------------------------------------------
  if (weeks < 20) {
    const seen = new Set(events.filter((e): e is LogEvent<'social'> => e.type === 'social' && e.data.reaction === 'happy').map((e) => e.data.itemId));
    const gap = SOCIAL_CATEGORIES.map((c) => ({ c, n: c.items.filter((i) => seen.has(i.id)).length })).sort((a, b) => a.n - b.n)[0];
    if (gap && gap.n < 2) {
      const pick = gap.c.items.find((i) => !seen.has(i.id) && !i.afterVaccination) ?? gap.c.items[0];
      tips.push({ id: `social-${gap.c.id}-${day}`, kind: 'social', priority: weeks >= 12 ? 65 : 45, title: `Socialise: ${gap.c.title.toLowerCase()}`, body: `Not much logged here yet. Today, try: ${pick.label}${pick.tip ? ` — ${pick.tip}` : '.'}`, route: '/social' });
    }
    if (profile?.dob) {
      const left = daysBetween(day, addDays(profile.dob, 16 * 7));
      if (left > 0 && left <= 14) tips.push({ id: `window-${day}`, kind: 'social', priority: 70, title: `${left} days of the sensitive window left`, body: 'Prioritise sounds, people and handling you haven’t ticked yet. Short, happy, often.', route: '/social' });
    }
  }

  // --- Weight & food --------------------------------------------------------------
  const lastWeigh = since('weight');
  if (weeks < 26 && now - lastWeigh > 14 * DAY)
    tips.push({ id: `weigh-${day}`, kind: 'health', priority: 40, title: 'Weigh-in due', body: 'A weigh-in every couple of weeks keeps food amounts right while he grows.', route: '/health/weight' });
  if (profile) {
    const stock = foodStock(profile.food, now);
    if (stock && stock.daysLeft <= 5) tips.push({ id: `food-${stock.runsOut}`, kind: 'food', priority: 85, title: stock.daysLeft === 0 ? 'Food runs out today' : stock.daysLeft === 1 ? 'Food runs out tomorrow' : `Food runs out in ${stock.daysLeft} days`, body: 'Order the same food now so there’s no sudden switch.', route: '/food' });
  }

  // --- Time of day --------------------------------------------------------------
  if (hour >= 17 && hour < 20 && weeks < 26)
    tips.push({ id: `witching-${day}`, kind: 'routine', priority: 30, title: 'Witching hour', body: 'Evenings bring zoomies and nipping. Toilet trip, a stuffed Kong or chew, then a crate nap usually settles it.' });
  if (household?.home?.type === 'flat' && weeks < 12 && hour >= 21)
    tips.push({ id: `bedtime-${day}`, kind: 'routine', priority: 35, title: 'Last trip before bed', body: 'Last toilet trip right before bed, then plan for a quiet night trip down (sometimes two at this age) — lights low, no play, straight back.' });

  // --- Age of the week -------------------------------------------------------------
  const wk = Object.keys(WEEKLY)
    .map(Number)
    .filter((w) => w <= weeks)
    .pop();
  if (wk !== undefined && weeks - wk < 2) {
    const [title, body] = WEEKLY[wk];
    tips.push({ id: `week-${wk}`, kind: 'age', priority: 50, title: `Week ${weeks}: ${title}`, body });
  }

  // --- Diary ------------------------------------------------------------------------
  if (home && age && weeks < 70 && hour >= 9 && hour < 20 && !events.some((e) => e.type === 'photo' && e.at > now - 7 * DAY))
    tips.push({ id: `portrait-${weeks}`, kind: 'routine', priority: 25, title: `Week ${weeks} portrait`, body: 'Same spot, same angle, once a week — it becomes a flipbook of him growing up.', route: '/diary' });

  return rank(tips);
}

function rank(tips: Tip[]): Tip[] {
  return tips.sort((a, b) => b.priority - a.priority);
}
